import type { FC, SyntheticEvent } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Chip,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  AccountBalance as AccountBalanceIcon,
  Assessment as ReportsIcon,
  IntegrationInstructions as IntegrationIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import CTAButton from '../components/common/CTAButton';
import FeatureCard from '../components/common/FeatureCard';

const HomePage: FC = () => {
  const features = [
    {
      icon: PhoneIcon,
      title: 'VoIP Integration',
      description: 'Seamless SIP.js softphone with predictive dialing, call recording, and compliance-aware call routing.',
    },
    {
      icon: SecurityIcon,
      title: 'Full Compliance',
      description: 'Built-in FDCPA, TCPA, and state-level compliance tracking. Never miss a regulation again.',
    },
    {
      icon: SpeedIcon,
      title: 'Fast Onboarding',
      description: 'Get your team up and running in days, not months. Automated setup and intuitive interface.',
    },
    {
      icon: AccountBalanceIcon,
      title: 'Account Management',
      description: 'Comprehensive debt account tracking with payment plans, settlements, and full audit trails.',
    },
    {
      icon: ReportsIcon,
      title: 'Analytics & Reports',
      description: 'Real-time dashboards, collector performance metrics, and executive reporting.',
    },
    {
      icon: IntegrationIcon,
      title: 'Easy Integrations',
      description: 'Connect to credit bureaus, payment processors, and your existing tools via API.',
    },
  ];

  const stats = [
    { value: 'FDCPA + TCPA', label: 'Compliance controls built in' },
    { value: 'AES-256', label: 'Encryption for all PII' },
    { value: 'Real-time', label: 'Cease-and-desist enforcement' },
    { value: 'Q3 2027', label: 'Launching' },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '90vh',
          display: 'flex',
          alignItems: 'center',
          background: 'radial-gradient(ellipse at 50% 0%, rgba(100, 181, 246, 0.15) 0%, transparent 70%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip
                label="Launching Q3 2027 · Try the live product today"
                size="small"
                sx={{
                  mb: 3,
                  bgcolor: 'rgba(100, 181, 246, 0.15)',
                  color: 'primary.main',
                  fontWeight: 600,
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                  mb: 3,
                  lineHeight: 1.2,
                }}
              >
                Compliance-first debt collection,
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    background: 'linear-gradient(135deg, #64b5f6 0%, #f48fb1 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  built and working today.
                </Box>
              </Typography>
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ mb: 4, maxWidth: 560, fontWeight: 400 }}
              >
                Debtornator bakes fail-closed FDCPA &amp; TCPA controls into every
                action. Explore the live demo and watch the compliance engine block
                a contact in real time.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <CTAButton to="/request-demo">
                  Try the Live Demo
                </CTAButton>
                <CTAButton to="/features" variant="outlined">
                  See Features
                </CTAButton>
              </Box>
              <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    Instant access — no install
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    Live product · real compliance
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '120%',
                    height: '120%',
                    background: 'radial-gradient(circle, rgba(100, 181, 246, 0.2) 0%, transparent 70%)',
                    zIndex: 0,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/dashboard-preview.png"
                  alt="Debtornator Dashboard"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: 4,
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                    position: 'relative',
                    zIndex: 1,
                  }}
                  onError={(e: SyntheticEvent<HTMLImageElement>) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #64b5f6 0%, #f48fb1 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 12 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" gutterBottom>
              Everything You Need
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              A complete solution for modern debt collection agencies,
              built from the ground up with compliance in mind.
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <FeatureCard
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  gradient={index === 0}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 12 }}>
        <Container maxWidth="md">
          <Card
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(100, 181, 246, 0.1) 0%, rgba(244, 143, 177, 0.1) 100%)',
              border: '1px solid rgba(100, 181, 246, 0.3)',
            }}
          >
            <Typography variant="h3" gutterBottom>
              Ready to Transform Your Collections?
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Explore a fully-seeded, read-only demo — including the compliance engine in action.
            </Typography>
            <CTAButton to="/request-demo" size="large">
              Try the Live Demo
            </CTAButton>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
